/**
 * @file       Comments mixin, used by the whole comments module.
 * @author     Appwapp <info@appwapp.com>
 * @copyright  © Centre collégial de développement de matériel didactique, Collège de Maisonneuve, 2021
 * @license    https://www.ccdmd.qc.ca
 * @version    1.0
 */

import { mapState } from "vuex";

/**
 * Comments mixin.
 *
 * @type  {Object}
 */
const commentsMixin = {
  data() {
    return {
      batchActionSelected: -1,
      show: false,
      submitDisabled: false,
      routerJobs: [
        "deliberation.byUrl",
        "deliberation.byUrl.answers",
        "deliberation.messages",
        "deliberation.suggestions"
      ]
    };
  },
  computed: {
    ...mapState({
      dlibr: state => state.deliberations.dlibr.data.data.find(d => d),
      comments: state => state.comments.comments,
      callComment: state => state.comments.comment.status,
      commentToEdit: state => state.deliberations.pubToEdit,
      messageToEdit: state => state.comments.message.data,
      profile: state =>
        state.auth.profile.data
          ? state.auth.profile.data.data.find(u => u)
          : {},
      flowDlibr: state => state.comments.flowDlibr,
      generalMsgs: state => state.comments.generalMsgs,
      privateMsgs: state => state.comments.privateMsgs,
      activeJobs: state => state.comments.activeJobs.data,
      isActiveJobs: state => state.comments.activeJobs.isActive
    }),

    /**
     * Check if comment status loading.
     *
     * @return   {Bool}  true or false
     */
    isLoading() {
      return this.comments.status === "fetching";
    },

    /**
     * Check if comment empty.
     *
     * @return   {Bool}  true or false
     */
    isEmpty() {
      return (
        this.comments.status === "success" && !this.comments.data.data.length
      );
    },

    /**
     * Check if user is a teacher.
     *
     * @return   {Bool}  true or false
     */
    isTeacher() {
      return this.profile.role === "teacher";
    },

    /**
     * Check if user is a student.
     *
     * @return   {Bool}  true or false
     */
    isStudent() {
      return this.profile.role === "student";
    },

    /**
     * Deliberation type map.
     *
     * @return   {Array}  Lookup array
     */
    typeMap() {
      return {
        1: { text: this.dlibr.positive, variant: "success", offset: 0 },
        2: { text: this.dlibr.negative, variant: "danger", offset: 2 },
        3: { text: this.dlibr.neutral, variant: "purple", offset: 1 }
      };
    },

    /**
     * Check if route is deliberation answer.
     *
     * @return   {Bool}  true or false
     */
    routeIsAnswer() {
      return this.$route.name == "deliberation.byUrl.answers";
    },

    /**
     * Check if route is deliberation by URL.
     *
     * @return   {Bool}  true or false
     */
    routeIsByUrl() {
      return this.$route.name === "deliberation.byUrl";
    },

    /**
     * Check if route is deliberation suggestions.
     *
     * @return   {Bool}  true or false
     */
    routeIsSuggestion() {
      return this.$route.name === "deliberation.suggestions";
    },

    /**
     * Check if route is deliberation messages.
     *
     * @return   {Bool}  true or false
     */
    routeIsMessages() {
      return this.$route.name === "deliberation.messages";
    },

    /**
     * Check if route is deliberation arborescence graph.
     *
     * @return   {Bool}  true or false
     */
    routeIsArborescenceGraph() {
      return this.$route.name === "deliberation.arborescenceGraph";
    },

    /**
     * Check if comment filter.
     *
     * @return   {Bool}  true or false
     */
    isFilter() {
      return (
        this.comments.filters.type ||
        this.comments.filters.user ||
        this.comments.filters.discussion ||
        this.comments.filters.flagged
      );
    },

    /**
     * Check if comment flagged.
     *
     * @return   {Bool}  true or false
     */
    isFlaggedFilter() {
      return Boolean(this.comments.filters.flagged);
    },

    /**
     * Jobs.
     *
     * @return   {Object}  Active jobs
     */
    jobs() {
      return {
        showSuggestionIcon: this.activeJobs.data.data
          ? this.activeJobs.data.data.find(j => j.type === 2)
          : false,
        showGlobalMsgIcon: this.activeJobs.data.data
          ? this.activeJobs.data.data.find(j => j.type === 1)
          : false
      };
    }
  },
  methods: {
    /**
     * Update comment state.
     *
     * @param   {int}  id     The comment id
     *
     * @return  {Promise}
     */
    updateStetaComment(id) {
      return new Promise((resolve, reject) => {
        const params = {};
        params.deliberation = this.dlibr.id;
        params.id = id
          ? id
          : this.commentToEdit.pub.message_to
          ? this.commentToEdit.pub.comment
          : this.commentToEdit.pub.id;
        if (this.$route.name == "deliberation.byUrl.answers") {
          params.root = this.$route.params.id;
        }
        this.$store
          .dispatch("comments/updateStateComment", params)
          .then(response => resolve(response))
          .catch(error => reject(error));
      });
    },

    /**
     * Toggle mode.
     */
    toggleMode() {
      this.$store.commit("comments/setFlowDlibr", {
        fields: { evalMode: !this.flowDlibr.evalMode }
      });
    },

    /**
     * Load previous.
     *
     * @param   {int}  ref     The comment reference
     */
    loadPrevious(ref) {
      this.$store.commit("comments/setFlowDlibr", {
        reset: true,
        fields: {
          currentPositionScroll: true
        }
      });
      this.$store.commit("comments/setCommentsFilters", {
        fields: {
          resourcesPerPage: Number.isInteger(this.comments.filters.resourcesPerPage) ? this.comments.filters.resourcesPerPage : 25,
          page:             this.comments.filters.page + 1
        }
      });
      this.$store.dispatch("comments/getComments").then(() => {
        this.$nextTick(() => {
          document.querySelector(`#comment${ref}`).scrollIntoView(true);
          window.scrollBy(0, this.position());
          this.$store.commit("comments/setFlowDlibr", {
            fields: { currentPositionScroll: false }
          });
        });
      });
    },

    /**
     * Move to deliberation by URL.
     */
    moveToByUrl() {
      this.$router.push({
        name: "deliberation.byUrl",
        params: {
          teacher: this.dlibr.teacher,
          url: this.dlibr.url
        }
      });
    },

    /**
     * Move to deliberation answers.
     *
     * @param   {int}  commentId     The comment id
     */
    moveToAnswers(commentId) {
      this.$router.push({
        name: "deliberation.byUrl.answers",
        params: {
          teacher: this.dlibr.teacher,
          url: this.dlibr.url,
          id: commentId
        }
      });
    },

    /**
     * Check if current user is the comment owner.
     *
     * @param   {Object}  comment     The comment
     *
     * @return  {Bool}    true or false
     */
    isCommentOwner(comment) {
      return this.profile.id === comment.user.id;
    },

    /**
     * Format the user's name.
     *
     * @param   {Object}  user     The user
     *
     * @return  {String}  Format: first name followed by the last name
     */
    formatNameUser(user) {
      return user.nickname ? user.nickname : `${user.name} ${user.last_name}`;
    },

    /**
     * Toggle mask comment.
     *
     * @param   {Object}  comment     The comment
     */
    toggleMaskComment(comment) {
      this.maskedLoading = true;
      if (comment.message_to) {
        this.$store
          .dispatch(`comments/putMessage`, {
            message_id: comment.id,
            masked: !comment.masked,
            flagged: comment.masked ? false : comment.flagged
          })
          .then(() => {
            this.maskedLoading = false;
          });
      } else {
        this.$store
          .dispatch(`comments/putComment`, {
            id: comment.id,
            masked: !comment.masked,
            flagged: comment.masked ? false : comment.flagged
          })
          .then(() => {
            this.maskedLoading = false;
          });
      }
    },

    /**
     * Go to filter.
     *
     * @param   {Object}  name     The filter name
     */
    goToFilter(name) {
      if (this.batchActionSelected != name) {
        this.batchActionSelected = name;
      } else {
        name = "deliberation.byUrl";
        this.batchActionSelected = -1;
      }
      this.$router.push({
        name,
        params: {
          teacher: this.dlibr.teacher,
          url: this.dlibr.url
        }
      });
    },

    /**
     * Filter comments.
     *
     * @param   {Object}    select    The select
     * @param   {Function}  hide      The hide action
     */
    filterComments(select, hide) {
      if (
        select[Object.keys(select)[0]] === this.batchActionSelected ||
        Object.keys(select)[0] == this.batchActionSelected
      ) {
        this.batchActionSelected = -1;
        select = {};
      } else {
        this.batchActionSelected =
          Object.keys(select)[0] == "type"
            ? select[Object.keys(select)[0]]
            : Object.keys(select)[0];
      }

      const isInMainFilter =
        !Object.keys(select)[0] ||
        Object.keys(select)[0] === "discussion" ||
        Object.keys(select)[0] === "user" ||
        Object.keys(select)[0] === "flagged";
      const root =
        this.routeIsAnswer && !isInMainFilter
          ? { root: this.$route.params.id }
          : {};

      const fields = {
        deliberation: this.dlibr.id,
        ...select,
        ...root
      };
      this.$store.commit("comments/setFlowDlibr", {
        reset: true,
        fields: { goLastComment: true }
      });
      if (
        (this.routeIsAnswer && isInMainFilter) ||
        (!this.routeIsByUrl && !this.routeIsAnswer)
      ) {
        this.$router.replace({
          query: fields
        });
        this.moveToByUrl();
      } else {
        this.$store.commit("comments/setCommentsFilters", {
          reset: true,
          fields: fields
        });
        this.$store.dispatch("comments/getComments");
        hide();
      }
    },

    /**
     * Process active jobs.
     */
    onActiveJobs() {
      this.callJobs();
      const jobsInterval = setInterval(
        function() {
          if (
            this.routerJobs.find(e => e === this.$route.name) &&
            this.isStudent
          ) {
            this.callJobs();
          } else {
            this.$store.commit("comments/setActiveJobs", false);
            clearInterval(jobsInterval);
          }
        }.bind(this),
        20000
      );
    },
    /**
     * Call jobs.
     */
    callJobs() {
      this.$store.commit("comments/setActiveJobsFilters", {
        reset: true,
        fields: {
          user: this.profile.id,
          deliberation: this.dlibr.id,
          generated: true
        }
      });

      this.$store.dispatch("comments/getActiveJobs").then();
    },

    /**
     * Go to reference element.
     *
     * @param   {Object}    ref       The reference
     * @param   {Function}  hide      The hide action
     *
     * @return  {Promise}
     */
    goToRefElement(ref) {
      document
        .querySelector(ref)
        .scrollIntoView({ block: "center", behavior: "smooth" });
      return new Promise(resolve => {
        setTimeout(() => {
          resolve(true);
        }, 1000);
      });
    },

    /**
     * Determine comment position according to current route, user's role and filters.
     *
     * @return  {int}   The position in pixels
     */
    position() {
      let pos = 0;
      if (this.isStudent && !this.isFilter && !this.routeIsAnswer) {
        pos = -150;
      } else if (this.isStudent && this.isFilter && !this.routeIsAnswer) {
        pos = -185;
      } else if (this.isStudent && this.routeIsAnswer && !this.isFilter) {
        pos = -200;
      } else if (this.isStudent && this.routeIsAnswer && this.isFilter) {
        pos = -250;
      } else if (this.isTeacher && !this.isFilter && !this.routeIsAnswer) {
        pos = -190;
      } else if (this.isTeacher && this.isFilter && !this.routeIsAnswer) {
        pos = -230;
      } else if (this.isTeacher && this.routeIsAnswer && !this.isFilter) {
        pos = -250;
      } else if (this.isTeacher && this.routeIsAnswer && this.isFilter) {
        pos = -285;
      }
      return pos;
    }
  }
};

export { commentsMixin };
